import type { RefObject } from "react";

import type { PickingInfo } from "@deck.gl/core";
import type { MapboxOverlay } from "@deck.gl/mapbox";
import type { MapGeoJSONFeature, MapRef } from "react-map-gl/maplibre";

import type {
  ModelOverlayFeature,
  OverlayFeatureTypes,
} from "@/features/editor/types";
import type { EventFeature } from "@/features/map/hooks/events/types";
import { Poi } from "@/libs/models";

const DEFAULT_PICKING_RADIUS = 10;
const METERS_PER_LNG_DEGREE_AT_EQUATOR = 111320;
const MODEL_CLICK_HIT_RADIUS_PX = 28;

interface FeatureAdapter {
  map(): EventFeature[];
}

class DeckGlFeatureAdapter implements FeatureAdapter {
  private features: PickingInfo[];
  private options: { featureTypes?: OverlayFeatureTypes[] };

  constructor({
    point,
    map,
    options = {},
  }: {
    point?: { x: number; y: number };
    map?: RefObject<MapboxOverlay>;
    options?: { featureTypes?: OverlayFeatureTypes[]; layerIds?: string[] };
  }) {
    if (!point || !map) {
      return;
    }
    const { x, y } = point;
    this.options = options;
    this.features = map.current.pickMultipleObjects({
      layerIds: options.layerIds,
      radius: DEFAULT_PICKING_RADIUS,
      x,
      y,
    }) as PickingInfo[];
  }

  public map(): EventFeature[] {
    if (!this.features) return [];
    return this.features
      .map((f) => {
        let feature;

        if (f.object instanceof Poi) {
          feature = f.object.toGeojson();
        } else {
          feature = f.object;
        }
        return {
          ...feature,
          properties: {
            ...feature?.properties,
            layerProvider: "deckgl",
          },
        } as EventFeature;
      })
      .filter((x) => x != null)
      .filter((x) => {
        if (this.options.featureTypes != null) {
          return this.options.featureTypes.includes(x.properties?.type);
        }
        return true;
      })
      .reduce((acc: EventFeature[], current) => {
        const exists = acc.some((item) => item.id === current.id);
        if (!exists) acc.push(current);
        return acc;
      }, []);
  }
}

export class MapLibreFeatureAdapter implements FeatureAdapter {
  private features: MapGeoJSONFeature[];
  private options: { featureTypes?: OverlayFeatureTypes[] };

  constructor({
    point,
    map,
    options = {
      layerIds: [],
    },
    features,
  }: {
    point?: { x: number; y: number };
    map?: MapRef;
    options?: { featureTypes?: OverlayFeatureTypes[]; layerIds?: string[] };
    features?: MapGeoJSONFeature[];
  }) {
    this.options = options;
    if (features) {
      this.features = features;
      return;
    }
    if (!point || !map) {
      return;
    }

    const { x, y } = point;
    this.features = map.queryRenderedFeatures([x, y], {
      layers: options.layerIds,
    });
  }

  map(): EventFeature[] {
    if (!this.features) return [];
    const filteredFeatures = this.features.filter((feature) => {
      if (this.options.featureTypes) {
        return this.options.featureTypes.includes(feature.properties?.type);
      } else {
        return true;
      }
    });

    const deduplicatedFeatures = this.getUniqueFeatures(filteredFeatures);

    return deduplicatedFeatures.map(
      (feature) =>
        ({
          geometry: feature.geometry,
          id: feature.properties.id,
          properties: {
            ...feature.properties,
            layerId: feature.layer.id,
            layerProvider: "maplibre",
            sourceId: feature.source,
          },
          type: feature.type,
        }) as EventFeature,
    );
  }

  private getFeatureKey(feature: MapGeoJSONFeature): string | null {
    const id = feature.id ?? feature.properties?.id;

    if (id == null) {
      return null;
    }

    return [feature.source ?? "", feature.layer?.id ?? "", String(id)].join(
      ":",
    );
  }

  /**
   * MapLibre may return duplicate rendered features when a polygon is split
   * across tile boundaries. Keep the first source/layer/id match and preserve
   * features without an id.
   * More info about this behaviour:
   * https://maplibre.org/maplibre-gl-js/docs/API/classes/Map/#queryrenderedfeatures
   */
  private getUniqueFeatures(
    features: MapGeoJSONFeature[],
  ): MapGeoJSONFeature[] {
    const uniqueFeatures = new Map<string, MapGeoJSONFeature>();
    const featuresWithoutKey: MapGeoJSONFeature[] = [];

    for (const feature of features) {
      const key = this.getFeatureKey(feature);

      if (!key) {
        featuresWithoutKey.push(feature);
        continue;
      }

      if (!uniqueFeatures.has(key)) {
        uniqueFeatures.set(key, feature);
      }
    }

    return [...uniqueFeatures.values(), ...featuresWithoutKey];
  }
}

export class ThreeJSAdapter implements FeatureAdapter {
  private features: EventFeature[];
  private options: { featureTypes?: OverlayFeatureTypes[] };

  constructor({
    point,
    map,
    options = {
      layerIds: [],
    },
    instances,
  }: {
    point?: { x: number; y: number };
    map?: MapRef;
    options?: { featureTypes?: OverlayFeatureTypes[]; layerIds?: string[] };
    instances?: ModelOverlayFeature[];
  }) {
    this.options = options;
    if (!point || !map || !instances) {
      return;
    }
    const { x, y } = point;
    const pickedInstance = this.findThreeJSInstanceByCartesianPoint({
      instances,
      layerIds: options?.layerIds,
      map,
      point: {
        x,
        y,
      },
    });
    if (!pickedInstance) {
      return;
    }
    this.features = [
      {
        geometry: pickedInstance.geometry,
        id: pickedInstance.id,
        properties: {
          ...pickedInstance.properties,
          id: pickedInstance.id,
          layerId: pickedInstance.properties.layerId,
          layerProvider: "maplibre",
          sourceId: pickedInstance.properties.sourceId,
        },
        type: "Feature",
      },
    ];
  }

  // TODO ALBA: this method is not very precise when you
  // start tinkering with the geometry of the model
  private findThreeJSInstanceByCartesianPoint({
    map,
    point,
    instances,
    layerIds,
  }: {
    map: MapRef;
    point: { x: number; y: number };
    instances: ModelOverlayFeature[];
    layerIds?: string[];
  }) {
    if (instances.length === 0) return null;

    const shouldPerformPicking =
      layerIds == null ||
      layerIds === undefined ||
      layerIds.some((layerId) => layerId === "map") ||
      instances.some((instance) =>
        layerIds.includes(instance.properties.layerId),
      );

    if (!shouldPerformPicking) {
      return;
    }

    const zoom = map.getZoom() ?? 0;
    let pickedInstance: ModelOverlayFeature | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;

    for (const instance of instances) {
      if (
        instance.properties.minZoom != null &&
        zoom < instance.properties.minZoom
      )
        continue;
      if (
        instance.properties.maxZoom != null &&
        zoom > instance.properties.maxZoom
      )
        continue;

      const screenPoint = map.project([
        instance.geometry.coordinates[0],
        instance.geometry.coordinates[1],
      ]);
      const dx = screenPoint.x - point.x;
      const dy = screenPoint.y - point.y;
      const distanceSq = dx * dx + dy * dy;

      const latitudeRadians =
        (instance.geometry.coordinates[1] * Math.PI) / 180;
      const cosLatitude = Math.max(Math.cos(latitudeRadians), 1e-6);
      const approximateModelRadiusMeters = Math.max(
        2,
        Math.min(Math.abs(instance.properties.scale) * 7, 36),
      );
      const longitudeDelta =
        approximateModelRadiusMeters /
        (METERS_PER_LNG_DEGREE_AT_EQUATOR * cosLatitude);
      const offsetPoint = map.project([
        instance.geometry.coordinates[0] + longitudeDelta,
        instance.geometry.coordinates[1],
      ]);
      const modelRadiusPx = Math.hypot(
        offsetPoint.x - screenPoint.x,
        offsetPoint.y - screenPoint.y,
      );
      const hitRadiusPx = Math.max(MODEL_CLICK_HIT_RADIUS_PX, modelRadiusPx);
      const hitRadiusSq = hitRadiusPx * hitRadiusPx;

      if (distanceSq > hitRadiusSq || distanceSq >= bestDistanceSq) continue;
      bestDistanceSq = distanceSq;
      pickedInstance = instance;
    }

    return pickedInstance;
  }

  map(): EventFeature[] {
    if (!this.features) return [];
    return this.features
      .filter((feature) => {
        if (this.options.featureTypes) {
          return this.options.featureTypes.includes(feature.properties?.type);
        } else {
          return true;
        }
      })
      .map(
        (feature) =>
          ({
            geometry: feature.geometry,
            id: feature.id,
            properties: feature.properties,
            type: feature.type,
          }) as EventFeature,
      );
  }
}

export const FeatureAdapterFactory = {
  createDeckGlAdapter: ({
    point,
    map,
    options = {},
  }: {
    point?: { x: number; y: number };
    map?: RefObject<MapboxOverlay>;
    options?: { featureTypes?: OverlayFeatureTypes[]; layerIds?: string[] };
  }) => {
    return new DeckGlFeatureAdapter({
      map,
      options,
      point,
    });
  },

  createMapLibreAdapter: ({
    features,
    point,
    map,
    options = {},
  }: {
    features?: MapGeoJSONFeature[];
    point?: { x: number; y: number };
    map?: MapRef;
    options?: { featureTypes?: OverlayFeatureTypes[]; layerIds?: string[] };
  }) => {
    return new MapLibreFeatureAdapter({
      features,
      map,
      options,
      point,
    });
  },

  createThreeJSAdapter: ({
    instances,
    point,
    map,
    options = {},
  }: {
    instances: ModelOverlayFeature[];
    point?: { x: number; y: number };
    map?: MapRef;
    options?: { featureTypes?: OverlayFeatureTypes[]; layerIds?: string[] };
  }) => {
    return new ThreeJSAdapter({
      instances,
      map,
      options,
      point,
    });
  },
};

export const filterFeaturesById = ({
  features,
  filterIds,
}: {
  features?: EventFeature[];
  filterIds?: EventFeature["id"][];
}) => {
  if (!filterIds || filterIds.length === 0) return features;

  if (filterIds.filter(Boolean).length === 0) return [];

  return features?.filter((feature) => filterIds.includes(feature.id));
};

export default FeatureAdapterFactory;
